import {
  ApproveAiDetectionEventProperties,
  RejectAiDetectionEventProperties,
} from "@/analytics/ai-detection-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { FaroAiChip } from "@/components/common/ai-assets/ai-chip";
import { useApproveRejectAiMarkup } from "@/hooks/use-ai-markup-approve-reject";
import { useReviewAnnotationInScene } from "@/hooks/use-review-annotation-in-scene";
import {
  AiAssetReviewStatus,
  AiGeneratedAnnotation,
} from "@/store/ai-asset-review/ai-asset-review-slice";
import {
  CircularProgress,
  CompassGoToIcon,
  FaroIconButton,
  FaroText,
  FontWeights,
  grey,
  NO_TRANSLATE_CLASS,
  PointCloudIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Card, CardContent, ListItem, Stack } from "@mui/material";
import { SxProps } from "@mui/system";
import { memo, useCallback, useMemo, type JSX } from "react";
import { AiAnnotationStatusDisplay } from "./ai-annotation-status-display";

type ThumbnailProps = {
  /** Image source URL for the thumbnail */
  src?: string | null;
  /** Alt text for the image */
  title: string;
};

const Thumbnail = memo(function Thumbnail({
  src,
  title,
}: ThumbnailProps): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        width: 100,
        height: 100,
        backgroundColor: grey[100],
        borderRadius: 1,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={title ? `${title} preview` : "Asset preview"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: 4,
          }}
        />
      ) : (
        <FaroText variant="bodyXS" color="text.secondary">
          Preview
        </FaroText>
      )}
    </Box>
  );
});

type InfoContentProps = {
  /** The annotation data to display */
  annotation: AiGeneratedAnnotation;
  /** Handler for navigating to the annotation in the scene */
  onGoTo(): void;
};

const InfoContent = memo(function InfoContent({
  annotation,
  onGoTo,
}: InfoContentProps): JSX.Element {
  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{ justifyContent: "space-between", height: "100%", width: "100%" }}
    >
      {/* Task Name */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <FaroText
          variant="heading14"
          noWrap
          fontWeight={FontWeights.SemiBold}
          color={grey[900]}
          className={NO_TRANSLATE_CLASS}
        >
          {annotation.title}
        </FaroText>
        <FaroIconButton
          aria-label="go to annotation"
          color="inherit"
          size="s"
          onClick={onGoTo}
          margin={0}
        >
          <CompassGoToIcon />
        </FaroIconButton>
      </Stack>

      {/* AI Generated Tag */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <FaroText
          variant="labelM"
          color={grey[600]}
          fontWeight={FontWeights.Bold}
        >
          AI generated tag
        </FaroText>
        <FaroAiChip label={annotation.tag.toUpperCase()} />
      </Stack>

      {/* Focus Area */}
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <FaroText variant="labelM" color={grey[600]}>
          Extracted from
        </FaroText>
        <PointCloudIcon color="secondary" sx={{ width: 16, height: 16 }} />
        <FaroText
          variant="bodyXS"
          color={grey[600]}
          noWrap
          className={NO_TRANSLATE_CLASS}
        >
          {annotation.area}
        </FaroText>
      </Stack>
    </Stack>
  );
});

type AiAnnotationListItemProps = {
  /** The AI generated annotation to display */
  annotation: AiGeneratedAnnotation;
  /** The extra styling to apply to the card container */
  sx?: SxProps;
};

/** @returns A single annotation item in the AI review list with approve/reject actions */
export function AiAnnotationListItem({
  annotation,
  sx,
}: AiAnnotationListItemProps): JSX.Element {
  const reviewAnnotationInScene = useReviewAnnotationInScene(annotation.markup);
  const { approve, reject, isRunning } = useApproveRejectAiMarkup();

  const isReviewed = useMemo(
    () => annotation.status !== AiAssetReviewStatus.pending,
    [annotation.status],
  );

  const handleGoTo = useCallback(() => {
    reviewAnnotationInScene(annotation.id);
  }, [reviewAnnotationInScene, annotation.id]);

  const onApproveAction = useCallback(async () => {
    const approvedLabel = await approve(annotation.markup);
    if (approvedLabel) {
      Analytics.track<ApproveAiDetectionEventProperties>(
        EventType.approveAiDetection,
        {
          assetType: approvedLabel.name,
          hasReviewNotes: false,
          via: "list-view",
        },
      );
    }
  }, [approve, annotation.markup]);

  const onRejectAction = useCallback(async () => {
    const rejectedLabel = await reject(annotation.markup);
    if (rejectedLabel) {
      Analytics.track<RejectAiDetectionEventProperties>(
        EventType.rejectAiDetection,
        {
          assetType: rejectedLabel.name,
          hasReviewNotes: false,
          via: "list-view",
        },
      );
    }
  }, [reject, annotation.markup]);

  return (
    <ListItem sx={{ p: 0, ...sx }}>
      <Card
        sx={{
          width: "100%",
          height: "calc(100% - 32px)",
          border: 0.5,
          borderColor: grey[200],
          boxShadow: "none",
          borderRadius: 0,
          mb: 4,
        }}
      >
        <CardContent sx={{ width: "100%", height: "100%", display: "flex" }}>
          <Stack
            direction="column"
            spacing={2}
            sx={{ alignItems: "flex-start", width: "100%", height: "100%" }}
          >
            {/* Main Content */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "flex-start", width: "100%" }}
            >
              <Thumbnail
                src={annotation.thumbnail ?? undefined}
                title={annotation.title}
              />
              <InfoContent annotation={annotation} onGoTo={handleGoTo} />
            </Stack>

            {/* Actions / Status */}
            <Stack
              direction="column"
              sx={{ alignItems: "flex-end", width: "100%" }}
            >
              {isRunning && (
                <Box
                  component="div"
                  sx={{ display: "flex", justifyContent: "center", my: 1 }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}

              {!isReviewed && !isRunning && (
                <Stack direction="row" spacing={1}>
                  <FaroIconButton
                    size="s"
                    color="error"
                    onClick={onRejectAction}
                    disabled={isRunning}
                    aria-label="Reject annotation"
                  >
                    <CloseIcon />
                  </FaroIconButton>
                  <FaroIconButton
                    size="s"
                    color="success"
                    onClick={onApproveAction}
                    disabled={isRunning}
                    aria-label="Approve annotation"
                  >
                    <CheckIcon />
                  </FaroIconButton>
                </Stack>
              )}

              {isReviewed && !isRunning && (
                <AiAnnotationStatusDisplay status={annotation.status} />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </ListItem>
  );
}
